import { useState } from "react";
import api from "@/lib/api";
import { extractApiError } from "@/components/PanelState";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, ArrowUpCircle } from "lucide-react";

// ============================================================================
// PromoteStageButton — platform_admin-only override to force a candidate
// to any stage in {1,2,3,4,5}. Backs the `POST /api/hiring/candidates/{id}
// /promote-to-stage` endpoint. Motivated by needing to fast-forward a
// test candidate to Stage 4 to validate the real Google Calendar flow.
// Every promote requires a reason (>=10 non-whitespace chars) which lands
// in `admin_audit_log`. Fires the full on_stage_entered hook (SLA timers,
// owner cache, workflow-history event), so this exercises the real
// transition machinery.
// ============================================================================

const STAGES = [
  { value: 1, label: "Stage 1 — Approval" },
  { value: 2, label: "Stage 2 — Outreach" },
  { value: 3, label: "Stage 3 — Phone Interview" },
  { value: 4, label: "Stage 4 — Owner/COO decision" },
  { value: 5, label: "Stage 5 — Meeting/Offer (needs Stage 4 approval)" },
];

export default function PromoteStageButton({ candidate, onPromoted }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stage, setStage] = useState(candidate?.stage || 1);
  const [reason, setReason] = useState("");

  const reasonTrimmedLen = reason.trim().length;
  const reasonOk = reasonTrimmedLen >= 10;
  const stageChanged = stage !== candidate?.stage;
  const canSubmit = reasonOk && stageChanged && !saving;

  async function submit() {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await api.post(
        `/hiring/candidates/${candidate.id}/promote-to-stage`,
        { stage: Number(stage), reason: reason.trim() },
      );
      toast.success(
        `Promoted from Stage ${data.prev_stage} to Stage ${data.new_stage}`,
      );
      setOpen(false);
      setReason("");
      onPromoted?.();
    } catch (e) {
      toast.error(extractApiError(e) || "Promote failed");
    } finally {
      setSaving(false);
    }
  }

  function handleOpenChange(next) {
    setOpen(next);
    if (next) {
      // Reset state on open so a previous half-submitted reason doesn't
      // persist across candidates.
      setStage(candidate?.stage || 1);
      setReason("");
    }
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => handleOpenChange(true)}
        data-testid="hiring-candidate-promote-btn"
      >
        <ArrowUpCircle className="w-4 h-4 mr-1.5" /> Promote to stage
      </Button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="sm:max-w-lg"
          data-testid="hiring-candidate-promote-modal"
        >
          <DialogHeader>
            <DialogTitle>Promote candidate to stage</DialogTitle>
          </DialogHeader>

          <div
            className="flex gap-2 items-start text-xs text-[#78350f] bg-[#fffbeb] border border-[#fbbf24] rounded p-3"
            data-testid="hiring-candidate-promote-warning"
          >
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              Platform-admin override. Fires the full stage-entry hook —
              SLA timers restart, owner cache updates, and a workflow
              event is appended. Every promote is written to the audit
              log with your reason. Not reversible via this UI.
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-[#4b5563] mb-1">
                Current stage
              </label>
              <div
                className="text-sm text-[#0e1420]"
                data-testid="hiring-candidate-promote-current-stage"
              >
                Stage {candidate?.stage}
              </div>
            </div>

            <div>
              <label
                htmlFor="promote-stage-select"
                className="block text-xs uppercase tracking-wide text-[#4b5563] mb-1"
              >
                New stage
              </label>
              <select
                id="promote-stage-select"
                value={stage}
                onChange={(e) => setStage(Number(e.target.value))}
                className="w-full border border-[#e5e7eb] rounded px-3 py-2 text-sm bg-white"
                data-testid="hiring-candidate-promote-stage-select"
              >
                {STAGES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              {!stageChanged && (
                <div className="text-xs text-[#4b5563] mt-1">
                  Pick a stage different from the current one.
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="promote-reason"
                className="block text-xs uppercase tracking-wide text-[#4b5563] mb-1"
              >
                Reason (≥10 characters, written to audit log)
              </label>
              <Textarea
                id="promote-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. manual override to test Google Calendar scheduling flow on Prod"
                rows={3}
                data-testid="hiring-candidate-promote-reason"
              />
              <div
                className={`text-xs mt-1 ${
                  reasonOk ? "text-[#166534]" : "text-[#4b5563]"
                }`}
                data-testid="hiring-candidate-promote-reason-counter"
              >
                {reasonTrimmedLen} / 10 minimum
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
              data-testid="hiring-candidate-promote-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={!canSubmit}
              data-testid="hiring-candidate-promote-submit"
            >
              {saving ? "Promoting…" : `Promote to Stage ${stage}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
